import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import adminService from '../../utils/adminService';
import SEO from '../../components/SEO';
import DashboardStats from './components/DashboardStats';
import ClientsTable from './components/ClientsTable';
import InvoicesTable from './components/InvoicesTable';
import TransactionsTable from './components/TransactionsTable';
import OnboardingStatus from './components/OnboardingStatus';
import PromocodeManager from '../../components/admin/PromocodeManager';
import PromocodeDecoderComponent from '../../components/admin/PromocodeDecoderComponent';
import PlatformManager from '../../components/admin/PlatformManager';
import PromocodeGeneratorComponent from '../../components/admin/PromocodeGeneratorComponent';
import Button from '../../components/ui/Button';
import { 
  Users, 
  FileText, 
  CreditCard, 
  TrendingUp,
  Plus,
  Settings,
  Gift
} from 'lucide-react';
import Icon from '../../components/AppIcon';


const AdminDashboard = () => {
  const { user, userProfile, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [activePromocodeTab, setActivePromocodeTab] = useState('manager');
  const [dashboardData, setDashboardData] = useState({
    stats: null,
    clients: [],
    invoices: [],
    transactions: [],
    onboarding: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!authLoading && user) {
      loadDashboardData();
    }
  }, [user, authLoading]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      const [statsRes, clientsRes, invoicesRes, transactionsRes, onboardingRes] = await Promise.all([
        adminService.getDashboardStats(),
        adminService.getClients(),
        adminService.getInvoices(),
        adminService.getTransactions(),
        adminService.getOnboardingStatus(),
      ]);

      setDashboardData({
        stats: statsRes.success ? statsRes.data : null,
        clients: clientsRes.success ? clientsRes.data : [],
        invoices: invoicesRes.success ? invoicesRes.data : [],
        transactions: transactionsRes.success ? transactionsRes.data : [],
        onboarding: onboardingRes.success ? onboardingRes.data : [],
      });

      if (!statsRes.success) {
        setError(statsRes.error || 'Failed to load dashboard data');
      }
    } catch (error) {
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: TrendingUp },
    { id: 'clients', label: 'Clients', icon: Users },
    { id: 'invoices', label: 'Invoices', icon: FileText },
    { id: 'transactions', label: 'Transactions', icon: CreditCard },
    { id: 'onboarding', label: 'Onboarding', icon: Settings },
    { id: 'promocodes', label: 'Promocodes', icon: Gift },
  ];

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Please sign in to access the admin dashboard</p>
          <Button onClick={() => window.location.href = '/auth/login'}>
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  if (userProfile?.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Access denied. Admin privileges required.</p>
          <Button onClick={() => window.location.href = '/'}>
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Admin Dashboard"
        description="Administrative dashboard for EmailValidator platform management. Monitor user activity, manage clients, view transactions, and oversee platform operations."
        keywords="admin dashboard, platform management, user management, transaction monitoring, admin panel, system administration, platform analytics"
        canonical="https://mailvalidator.octalchip.com/admin-dashboard"
      />
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
                <p className="text-sm text-gray-500">
                  Welcome back, {userProfile?.full_name}
                </p>
              </div>
              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  onClick={() => loadDashboardData()}
                >
                  Refresh
                </Button>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Client
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        <div className="space-y-6">
          {activeTab === 'overview' && (
            <DashboardStats stats={dashboardData.stats} />
          )}
          
          {activeTab === 'clients' && (
            <ClientsTable 
              clients={dashboardData.clients} 
              onRefresh={loadDashboardData}
            />
          )}
          
          {activeTab === 'invoices' && (
            <InvoicesTable 
              invoices={dashboardData.invoices} 
              onRefresh={loadDashboardData}
            />
          )}
          
          {activeTab === 'transactions' && (
            <TransactionsTable 
              transactions={dashboardData.transactions} 
              onRefresh={loadDashboardData}
            />
          )}
          
          {activeTab === 'onboarding' && (
            <OnboardingStatus 
              onboarding={dashboardData.onboarding} 
              onRefresh={loadDashboardData}
            />
          )}
          
          {activeTab === 'promocodes' && (
            <div className="space-y-6">
              {/* Promocode Sub-tabs */}
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                  {[
                    { id: 'manager', label: 'Import/Export Promocodes', icon: Gift },
                    { id: 'generator', label: 'Generate Promocodes', icon: Plus },
                    { id: 'platforms', label: 'Platform Manager', icon: Settings },
                    { id: 'decoder', label: 'Decode Promocodes', icon: FileText }
                  ].map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActivePromocodeTab(tab.id)}
                        className={`${
                          activePromocodeTab === tab.id
                            ? 'border-blue-500 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
                      >
                        <Icon className="w-4 h-4 mr-2" />
                        {tab.label}
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Promocode Tab Content */}
              {activePromocodeTab === 'manager' && <PromocodeManager />}
              {activePromocodeTab === 'generator' && <PromocodeGeneratorComponent />}
              {activePromocodeTab === 'platforms' && <PlatformManager />}
              {activePromocodeTab === 'decoder' && <PromocodeDecoderComponent />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;